// Federal-grade secure system login - Full government standards
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ssoAPI } from '@/lib/api';

const PROVIDER_STYLES = {
  logingov: {
    label: 'Sign in with Login.gov',
    bg: '#1B2A4A',
    hoverBg: '#2D3F5F',
    fg: '#FFFFFF',
  },
  okta: {
    label: 'Sign in with Okta Gov',
    bg: '#007DC1',
    hoverBg: '#005F92',
    fg: '#FFFFFF',
  },
  azure: {
    label: 'Sign in with Azure AD Gov',
    bg: '#0078D4',
    hoverBg: '#005A9E',
    fg: '#FFFFFF',
  },
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Session expiry banner (set by axios interceptor on 401)
  const [sessionExpired, setSessionExpired] = useState(false);

  // SSO providers
  const [ssoProviders, setSsoProviders] = useState([]);
  const [ssoLoading, setSsoLoading] = useState({});

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromStorage = sessionStorage.getItem('session_expired') === '1';
      if (params.get('reason') === 'session_expired' || fromStorage) {
        setSessionExpired(true);
        sessionStorage.removeItem('session_expired');
      }
    } catch (_) { /* ignore */ }

    let alive = true;
    ssoAPI
      .listProviders()
      .then((res) => {
        if (alive) setSsoProviders(res.data?.providers || []);
      })
      .catch(() => {
        if (alive) setSsoProviders([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleSSOClick = async (providerId) => {
    setError('');
    setSsoLoading((s) => ({ ...s, [providerId]: true }));
    try {
      const res = await ssoAPI.initiateLogin(providerId);
      const authUrl = res.data?.auth_url;
      if (!authUrl) throw new Error('Missing auth_url');
      window.location.href = authUrl;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Unable to start SSO sign-in.';
      setError(detail);
      setSsoLoading((s) => ({ ...s, [providerId]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login({ email, password });
      if (result.requiresMFA) {
        navigate('/mfa-verify');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed. Verify credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel - Branded Navy Panel (60%) */}
      <div className="lg:w-3/5 bg-[#1B2A4A] relative overflow-hidden flex flex-col justify-between p-8 lg:p-16 min-h-[300px] lg:min-h-screen">
        {/* Subtle geometric pattern background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.03) 35px, rgba(255,255,255,.03) 70px)`
          }} />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Logo */}
          <div className="mb-8">
            <img 
              src="/unifed-logo-transparent.png" 
              alt="UniFed One" 
              className="h-24 lg:h-32 w-auto brightness-0 invert"
            />
          </div>

          {/* Tagline and Description */}
          <div className="max-w-xl">
            <h1 className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Federal Retirement<br />Management System
            </h1>
            <p className="text-lg text-[#B8C5D8] leading-relaxed mb-8">
              Secure, compliant recordkeeping platform for defined contribution retirement plans administered under federal authority.
            </p>
            
            {/* Features */}
            <div className="space-y-4 text-[#B8C5D8]">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-[#B8860B]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Zero-trust security architecture</span>
              </div>
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-[#B8860B]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">FISMA-compliant infrastructure</span>
              </div>
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-[#B8860B]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Multi-factor authentication required</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Compliance Notice */}
        <div className="relative z-10 mt-auto">
          <div className="border-t border-[#2D3F5F] pt-6">
            <p className="text-xs text-[#8B95A5] leading-relaxed">
              This system operates under the authority of the U.S. Office of Personnel Management (OPM) and complies with all applicable federal regulations including ERISA, IRC Section 401(a), and OMB Circular A-123.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form (40%) */}
      <div className="lg:w-2/5 bg-white flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md">
          {/* Government Authorization Banner */}
          <div className="bg-[#FFF9E6] border-l-4 border-[#B8860B] px-4 py-3 mb-8">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#B8860B] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-semibold text-[#92400E]">U.S. Government System — Authorized Use Only</span>
            </div>
          </div>

          {/* Form Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#1B2A4A] mb-2">
              Secure Sign In
            </h2>
            <p className="text-sm text-[#6B7280]">
              Enter your credentials to access your retirement account
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {sessionExpired && (
              <div
                className="bg-[#FFF9E6] border border-[#B8860B] px-4 py-3 rounded"
                data-testid="session-expired-banner"
                role="status"
              >
                <div className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 text-[#B8860B] flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .2.08.39.22.53l3 3a.75.75 0 101.06-1.06L10.75 9.69V5z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-[#92400E]">
                      Your session has expired
                    </p>
                    <p className="text-xs text-[#92400E] mt-0.5">
                      For your security we signed you out after inactivity. Please sign in again.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {error && (
              <div className="bg-[#FEF2F2] border border-[#FCA5A5] px-4 py-3 rounded">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-[#DC2626] text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* User ID Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-[#1B2A4A] mb-2">
                User ID
              </label>
              <input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your.name@agency.gov"
                className="w-full h-12 px-4 bg-white border-2 border-[#D1D5DB] rounded text-[#1B2A4A] text-base focus:outline-none focus:border-[#1B2A4A] focus:ring-2 focus:ring-[#1B2A4A]/20 transition-all"
                autoComplete="email"
              />
            </div>

            {/* Password Field with Toggle */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-[#1B2A4A] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-12 px-4 pr-12 bg-white border-2 border-[#D1D5DB] rounded text-[#1B2A4A] text-base focus:outline-none focus:border-[#1B2A4A] focus:ring-2 focus:ring-[#1B2A4A]/20 transition-all"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1B2A4A] transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-12 bg-[#1B2A4A] hover:bg-[#2D3F5F] active:bg-[#0F1623] text-white text-base font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Sign In'
              )}
            </button>

            {/* Forgot Credentials */}
            <div className="text-center">
              <p className="text-sm text-[#6B7280]">
                Forgot credentials?{' '}
                <span className="text-[#1B2A4A] font-semibold">Contact your plan administrator</span>
              </p>
            </div>
          </form>

          {/* SSO Federal Identity Providers */}
          {ssoProviders.length > 0 && (
            <div className="mt-8" data-testid="sso-section">
              <div className="relative mb-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E5E7EB]" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs font-semibold tracking-wider text-[#6B7280] uppercase">
                    Or sign in with Federal SSO
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {ssoProviders.map((p) => {
                  const style = PROVIDER_STYLES[p.id] || {
                    label: `Sign in with ${p.display_name}`,
                    bg: '#1B2A4A',
                    hoverBg: '#2D3F5F',
                    fg: '#FFFFFF',
                  };
                  const disabled = !p.enabled || !!ssoLoading[p.id];
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`sso-button-${p.id}`}
                      aria-label={style.label}
                      disabled={disabled}
                      onClick={() => handleSSOClick(p.id)}
                      className="w-full h-12 flex items-center justify-center gap-2 rounded text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: p.enabled ? style.bg : '#9CA3AF',
                        color: style.fg,
                      }}
                      onMouseEnter={(e) => {
                        if (!disabled) e.currentTarget.style.backgroundColor = style.hoverBg;
                      }}
                      onMouseLeave={(e) => {
                        if (!disabled) e.currentTarget.style.backgroundColor = style.bg;
                      }}
                    >
                      {ssoLoading[p.id] ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Redirecting…
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          {p.enabled ? style.label : `${style.label} (not configured)`}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>

              <p
                className="mt-4 text-xs text-[#6B7280] leading-relaxed"
                data-testid="sso-federal-disclosure"
              >
                SSO sign-in is offered through FedRAMP-authorized identity providers under OIDC with PKCE (RFC 7636) and id_token JWKS signature verification. Your agency or department provisions access directly through these identity providers.
              </p>
            </div>
          )}

          {/* CAC/PIV Notice */}
          <div className="mt-8 p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-[#B8860B] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-[#1B2A4A] mb-1">
                  CAC/PIV Card Authentication
                </p>
                <p className="text-xs text-[#6B7280]">
                  Available for eligible federal employees and contractors
                </p>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-8 p-4 bg-[#F9FAFB] border-l-4 border-[#DC2626]">
            <p className="text-xs text-[#374151] leading-relaxed">
              <span className="font-semibold text-[#DC2626]">SECURITY NOTICE:</span> This system is for authorized U.S. Government use only. Unauthorized access is prohibited and subject to criminal prosecution under{' '}
              <span className="font-semibold">18 U.S.C. § 1030</span>. All activities are monitored and recorded. By using this system, you consent to security monitoring and recording.
            </p>
          </div>

          {/* Registration Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#6B7280]">
              New enrollment?{' '}
              <Link to="/register" className="text-[#1B2A4A] hover:text-[#B8860B] font-semibold transition-colors">
                Register access
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
